import {
    CircleConsentConfigScopeEnum,
    CircleConsentTypes,
} from '@calm/evexia-web-sdk';
import { ConsentContext } from '@rhg/providers/registration/consent';
import { useContext, useState } from 'react';
import {
    AccountConsentConfigs,
    AccountConsentTypes,
    AccountConsentVersions,
    CircleConsentConfigs,
    ConsentAcceptance,
    ConsentContextProps,
    ConsentContextState,
    ConsentFlow,
    ConsentRequirement,
    ConsentTypes,
    ConsentVersions,
    OverlappingConsentTypes,
} from './types';

function makeDefaultCircleConsentConfigs(): CircleConsentConfigs {
    return Object.values(CircleConsentTypes).reduce((acc, type) => {
        acc[type] = undefined;
        return acc;
    }, {} as CircleConsentConfigs);
}

function makeDefaultAccountConsentConfigs(): AccountConsentConfigs {
    return Object.values(AccountConsentTypes).reduce((acc, type) => {
        acc[type] = undefined;
        return acc;
    }, {} as AccountConsentConfigs);
}

function makeDefaultConsentAcceptance(): ConsentAcceptance {
    return Object.values(ConsentTypes).reduce((acc, type) => {
        acc[type] = false;
        return acc;
    }, {} as ConsentAcceptance);
}

function makeDefaultAccountConsentVersions(): AccountConsentVersions {
    return Object.values(AccountConsentTypes).reduce((acc, type) => {
        acc[type] = 0;
        return acc;
    }, {} as AccountConsentVersions);
}

/** Determines which consents are required based on the user's account, sponsor, and already accepted consents */
export function getConsentContext({
    accountConsents,
    accountUserConsents,
    sponsorConsents,
    currentSponsorConsents,
    shouldUseCurrentSponsor,
}: ConsentContextProps): ConsentRequirement {
    const sponsorConsentConfigs =
        sponsorConsents?.reduce<CircleConsentConfigs>(
            (consentConfigs, consentConfig) => {
                // Use the consent config with the highest version and acceptedVersion
                if (
                    !consentConfigs[consentConfig.type] ||
                    consentConfigs[consentConfig.type]!.version >
                        consentConfig.version ||
                    (consentConfigs[consentConfig.type]!.version ===
                        consentConfig.version &&
                        (consentConfigs[consentConfig.type]?.acceptedVersion ??
                            0) > (consentConfig.acceptedVersion ?? 0))
                ) {
                    consentConfigs[consentConfig.type] = consentConfig;
                }

                return consentConfigs;
            },
            makeDefaultCircleConsentConfigs()
        ) ?? makeDefaultCircleConsentConfigs();

    const accountConsentConfigs = accountConsents.reduce<AccountConsentConfigs>(
        (consentConfigs, consent) => {
            if (
                !consentConfigs.tos ||
                (consent?.minTosVersion ?? 0) > consentConfigs.tos.version
            ) {
                consentConfigs.tos = {
                    version: consent.minTosVersion ?? 0,
                    link: consent.tosLink ?? '',
                };
            }
            if (
                !consentConfigs.privacy ||
                (consent?.minPrivacyVersion ?? 0) >
                    consentConfigs.privacy.version
            ) {
                consentConfigs.privacy = {
                    version: consent.minPrivacyVersion ?? 0,
                    link: consent.privacyLink ?? '',
                };
            }
            if (
                !consentConfigs.chdpp ||
                (consent?.minCHDPPVersion ?? 0) > consentConfigs.chdpp.version
            ) {
                consentConfigs.chdpp = {
                    version: consent.minCHDPPVersion ?? 0,
                    link: consent.chdppLink ?? '',
                };
            }
            if (
                !consentConfigs.uk_international ||
                (consent?.minUKInternationalVersion ?? 0) >
                    consentConfigs.uk_international.version
            ) {
                consentConfigs.uk_international = {
                    version: consent.minUKInternationalVersion ?? 0,
                    link: consent.ukInternationalLink ?? '',
                };
            }
            return consentConfigs;
        },
        makeDefaultAccountConsentConfigs()
    );

    const userAccountConsentVersion =
        accountUserConsents.reduce<AccountConsentVersions>(
            (maxConsentedVersion, consent) => ({
                tos: Math.max(maxConsentedVersion.tos, consent.tosVersion ?? 0),
                privacy: Math.max(
                    maxConsentedVersion.privacy,
                    consent.privacyVersion ?? 0
                ),
                chdpp: Math.max(
                    maxConsentedVersion.chdpp,
                    consent.chdppVersion ?? 0
                ),
                uk_international: Math.max(
                    maxConsentedVersion.uk_international,
                    consent.ukInternationalVersion ?? 0
                ),
            }),
            makeDefaultAccountConsentVersions()
        );

    return Object.values(ConsentTypes).reduce<ConsentRequirement>(
        (consentContext, consentType: ConsentTypes) => {
            let requiredVersion = 0;
            let acceptedVersion = 0;
            let link = '';
            let scope: CircleConsentConfigScopeEnum = 'account';

            if (
                Object.values(CircleConsentTypes).includes(
                    consentType as CircleConsentTypes
                ) &&
                shouldUseCurrentSponsor?.includes(
                    consentType as CircleConsentTypes
                )
            ) {
                // Override with current sponsor if needed
                const currentSponsorConsentConfig =
                    currentSponsorConsents?.find(
                        (consent) => consent.type === consentType
                    );
                requiredVersion = currentSponsorConsentConfig?.version ?? 0;
                acceptedVersion =
                    currentSponsorConsentConfig?.acceptedVersion ?? 0;
                link = currentSponsorConsentConfig?.url ?? '';
                scope = currentSponsorConsentConfig?.scope ?? 'account';
            } else if (
                Object.values(OverlappingConsentTypes).includes(
                    consentType as OverlappingConsentTypes
                )
            ) {
                // Use sponsor consent if any sponsor consents exist and we expect an overlap fallback to account consent if there is are no sponsor consents of any type
                const overlappedConsentType =
                    consentType as OverlappingConsentTypes;
                const isSponsorConsent = !!sponsorConsents;
                requiredVersion = isSponsorConsent
                    ? sponsorConsentConfigs[overlappedConsentType]?.version ?? 0
                    : accountConsentConfigs[overlappedConsentType]?.version ??
                      0;
                acceptedVersion = Math.max(
                    sponsorConsentConfigs[overlappedConsentType]
                        ?.acceptedVersion ?? 0,
                    userAccountConsentVersion[overlappedConsentType]
                );
                link = isSponsorConsent
                    ? sponsorConsentConfigs[overlappedConsentType]?.url ?? ''
                    : accountConsentConfigs[overlappedConsentType]?.link ?? '';
                scope =
                    sponsorConsentConfigs[overlappedConsentType]?.scope ??
                    'account';
            } else if (
                Object.values(CircleConsentTypes).includes(
                    consentType as CircleConsentTypes
                )
            ) {
                // Use sponsor consent if it exists
                const circleConsentType = consentType as CircleConsentTypes;
                requiredVersion =
                    sponsorConsentConfigs[circleConsentType]?.version ?? 0;
                acceptedVersion =
                    sponsorConsentConfigs[circleConsentType]?.acceptedVersion ??
                    0;
                link = sponsorConsentConfigs[circleConsentType]?.url ?? '';
                scope =
                    sponsorConsentConfigs[circleConsentType]?.scope ??
                    'account';
            } else {
                // Account consent is the fallthrough case
                const accountConsentType = consentType as AccountConsentTypes;
                requiredVersion =
                    accountConsentConfigs[accountConsentType]?.version ?? 0;
                acceptedVersion =
                    userAccountConsentVersion[accountConsentType] ?? 0;
                link = accountConsentConfigs[accountConsentType]?.link ?? '';
            }
            consentContext[consentType] = {
                consentType,
                acceptedVersion,
                requiredVersion,
                link,
                scope,
                isRequired: requiredVersion > acceptedVersion,
                hasRequirement: requiredVersion > 0,
                isReconsent:
                    !!acceptedVersion && requiredVersion > acceptedVersion,
            };
            return consentContext;
        },
        {} as ConsentRequirement
    );
}

export function getConsentSubmission({
    consentRequirements,
    consentTypes = ConsentFlow,
}: {
    consentRequirements: ConsentRequirement;
    consentTypes?: ConsentTypes[];
}) {
    return Object.values(consentRequirements).reduce<ConsentVersions>(
        (consentVersions, { consentType, requiredVersion }) => {
            if (requiredVersion && consentTypes.includes(consentType)) {
                consentVersions[consentType] = requiredVersion;
            }
            return consentVersions;
        },
        {} as ConsentVersions
    );
}

/**
 * loop through the consentFlow array and display the first one that does not satisfy the requirements
 */
export function getNextConsentType({
    consentRequirements,
    consentAcceptance,
    consentFlow = ConsentFlow,
    reverse = false,
}: {
    consentRequirements: ConsentRequirement;
    /** Additional map of accepted/available consents */
    consentAcceptance?: ConsentAcceptance;
    consentFlow?: ConsentTypes[];
    /** Whether the consent needs to just have a required version (false), or whether it needs to have the required version > accepted version (true) */
    needsRequired?: boolean;
    reverse?: boolean;
}) {
    return (reverse ? consentFlow.toReversed() : consentFlow).find(
        (consentType) =>
            consentRequirements[consentType] &&
            consentRequirements[consentType].hasRequirement &&
            consentRequirements[consentType].isRequired &&
            !consentAcceptance?.[consentType]
    );
}

export function getNextConsentTypeInFlow(
    consentType: ConsentTypes,
    consentFlow = ConsentFlow
) {
    const currentIndex = consentFlow.indexOf(consentType as CircleConsentTypes);
    return currentIndex !== -1 ? consentFlow.slice(currentIndex + 1) : [];
}

export function useConsentContext(
    props: ConsentContextProps
): ConsentContextState {
    const [hasAcceptedConsent, _setHasAcceptedConsent] = useState(
        makeDefaultConsentAcceptance()
    );
    const setHasAcceptedConsent = (type: ConsentTypes, value: boolean) =>
        _setHasAcceptedConsent((prev) => ({
            ...prev,
            [type]: value,
        }));

    const consentRequirements = getConsentContext(props);

    return {
        consentRequirements,
        nextConsentType: getNextConsentType({
            consentRequirements,
            consentAcceptance: hasAcceptedConsent,
        }),
        hasAcceptedConsent,
        setHasAcceptedConsent,
    };
}

/** Uses context from the closest <ConsentContextProvider> */
export function useProvidedConsentContext() {
    return useContext(ConsentContext);
}
